import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { post as report2026, reportContent } from '@/data/blog-posts/2026-building-codes-report';
import ReportHeader from '@/components/report/ReportHeader';
import ReportTableOfContents from '@/components/report/ReportTableOfContents';
import CityAccordion from '@/components/report/CityAccordion';
import BuilderCitySection from '@/components/report/BuilderCitySection';
import KeyTakeawaysBox from '@/components/report/KeyTakeawaysBox';
import { articleSchema, breadcrumbListSchema } from '@/lib/structured-data';

const ReportPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  // For now, we only have one report
  if (slug !== '2026-building-codes-report') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container-custom section-padding">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Report Not Found</h1>
            <p className="mb-8">The report you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/blog')}>
              Back to Insights
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const post = report2026;
  const tocItems = post.tableOfContents?.map(item => ({
    id: item.id,
    title: item.title,
    level: item.level
  })) || [];

  const currentPageUrl = `https://www.fordje.com/report/${post.slug}`;
  const metaTitle = `${post.title} | Fordje`;
  const metaDescription = post.metaDescription || post.excerpt;

  const structuredData = articleSchema({
    type: 'Report',
    title: post.title,
    description: metaDescription,
    slug: post.slug,
    pathPrefix: '/report',
    coverImage: post.coverImage,
    datePublished: post.date,
    author: post.author,
    categories: post.categories,
    keywords: post.seoKeywords,
    readTimeMinutes: post.readTime,
  });

  const breadcrumbData = breadcrumbListSchema([
    { name: 'Home', path: '/' },
    { name: 'Insights', path: '/blog' },
    { name: post.title, path: `/report/${post.slug}` },
  ]);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={post.seoKeywords?.join(', ')} />
        <link rel="canonical" href={currentPageUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentPageUrl} />
        <meta property="og:image" content={post.coverImage} />
        <meta property="og:site_name" content="Fordje" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author.name} />
        {post.categories.map((category, index) => (
          <meta property="article:tag" content={category} key={`article:tag:${index}`} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.coverImage} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      </Helmet>
      
      <Header variant="light" />
      
      <ReportHeader 
        title={post.title}
        excerpt={post.excerpt}
        date={post.date}
        readTime={post.readTime}
        author={post.author}
        categories={post.categories}
      />
      
      <main className="flex-grow bg-slate-50">
        <div className="container-custom py-12">
          <div className="flex gap-8">
            {/* Sticky TOC Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <ReportTableOfContents items={tocItems} />
                <div className="mt-6 pt-6 border-t">
                  <Link 
                    to="/blog" 
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-fordje-blue transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to all Insights
                  </Link>
                </div>
              </div>
            </aside>
            
            {/* Main Content */}
            <article className="flex-1 max-w-4xl">
              <Card className="bg-white p-8 md:p-12 shadow-sm">
                {/* Introduction */}
                <section id="introduction" className="scroll-mt-28 mb-12">
                  <h2 className="text-2xl font-bold text-fordje-navy mb-6">Introduction</h2>
                  <div 
                    className="prose prose-lg max-w-none [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: reportContent.introduction }}
                  />
                </section>
                
                <Separator className="my-12" />
                
                {/* Section 1: Clean Tech Installers */}
                <section id="section-1" className="scroll-mt-28 mb-12">
                  <h2 className="text-3xl font-bold text-fordje-navy mb-4">
                    {reportContent.sections[0].title}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    {reportContent.sections[0].intro}
                  </p>
                  
                  {reportContent.sections[0].states?.map((state) => (
                    <div key={state.id} id={state.id} className="scroll-mt-28 mb-12">
                      <h3 className="text-2xl font-bold text-fordje-navy mb-3 pb-2 border-b">
                        {state.name}
                      </h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {state.intro}
                      </p>
                      
                      {(state.cities as any[]).map((city) => (
                        <CityAccordion
                          key={city.id}
                          id={city.id}
                          cityName={city.name}
                          overview={city.overview}
                          sections={city.installerTypes}
                        />
                      ))}
                    </div>
                  ))}
                  
                  <div id="clean-tech-takeaways" className="scroll-mt-28">
                    <KeyTakeawaysBox 
                      title="Key Takeaways for Clean Tech Installers"
                      takeaways={reportContent.sections[0].keyTakeaways || []}
                      variant="highlight"
                    />
                  </div>
                </section>
                
                <Separator className="my-12" />
                
                {/* Section 2: Residential Homebuilders */}
                <section id="section-2" className="scroll-mt-28 mb-12">
                  <h2 className="text-3xl font-bold text-fordje-navy mb-4">
                    {reportContent.sections[1].title}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    {reportContent.sections[1].intro}
                  </p>
                  
                  {reportContent.sections[1].states?.map((state) => (
                    <div key={state.id} id={state.id} className="scroll-mt-28 mb-12">
                      <h3 className="text-2xl font-bold text-fordje-navy mb-3 pb-2 border-b">
                        {state.name}
                      </h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {state.intro}
                      </p>
                      
                      {(state.cities as any[]).map((city) => (
                        <BuilderCitySection
                          key={city.id}
                          id={city.id}
                          cityName={city.name}
                          overview={city.overview}
                          requirements={city.requirements}
                        />
                      ))}
                    </div>
                  ))}
                  
                  <div id="builder-takeaways" className="scroll-mt-28">
                    <KeyTakeawaysBox 
                      title="Key Takeaways for Homebuilders"
                      takeaways={reportContent.sections[1].keyTakeaways || []}
                      variant="highlight"
                    />
                  </div>
                </section>
                
                <Separator className="my-12" />
                
                {/* Conclusion */}
                <section id="conclusion" className="scroll-mt-28">
                  <h2 className="text-2xl font-bold text-fordje-navy mb-6">Conclusion</h2>
                  <div 
                    className="prose prose-lg max-w-none [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: reportContent.conclusion }}
                  />
                </section>
              </Card>
              
              {/* CTA Section */}
              <div className="mt-8 bg-gradient-to-r from-fordje-navy to-fordje-navy-light text-white p-8 rounded-xl">
                <h3 className="text-xl font-bold mb-3">Stay Ahead of Code Changes</h3>
                <p className="text-white/80 mb-6">
                  Get real-time access to building codes and local amendments across 28,000+ jurisdictions with Fordje.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/request-demo')}
                    className="bg-white text-fordje-navy hover:bg-white/90"
                  >
                    Request a Demo
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/blog')}
                    className="border-white/30 text-white hover:bg-white/10"
                  >
                    More Insights
                  </Button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReportPost;
